<template>
  <v-container class="contact-page" fluid>
    <v-popup-loading :isLoading="isPageLoading" />
    <div class="head-wrap mb-4">
      <div>
        <h1 class="headline mb-1">{{ isEditMode ? 'Edit Award' : 'Create Award' }}</h1>
        <p class="subhead mb-0">Fields marked <span class="required">*</span> are required.</p>
      </div>
      <div class="top-actions">
        <v-btn outlined class="mr-2" @click="onCancel">Cancel</v-btn>
        <v-btn v-if="canSave" color="primary" :loading="isSaving" :disabled="!isFormValid" @click="onSave">Save</v-btn>
      </div>
    </div>

    <v-card class="form-card">
      <div class="form-card-head">Scope</div>
      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12" md="4"><label>BRANCH <span class="required">*</span></label><v-select dense outlined hide-details :items="scopeOptions" item-text="text" item-value="value" v-model="form.org_codename"/></v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="form-card">
      <div class="form-card-head">Content</div>
      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12" md="6"><label>AWARD TITLE <span class="lang">TH</span> <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.title_th" placeholder="เช่น การรับรองมาตรฐาน JCI"/></v-col>
          <v-col cols="12" md="6"><label>AWARD TITLE <span class="lang">EN</span> <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.title_en" placeholder="e.g. JCI Accreditation"/></v-col>
          <v-col cols="12" md="6"><label>DESCRIPTION <span class="lang">TH</span></label><v-textarea dense outlined hide-details rows="3" v-model="form.description_th" placeholder="รายละเอียด..."/></v-col>
          <v-col cols="12" md="6"><label>DESCRIPTION <span class="lang">EN</span></label><v-textarea dense outlined hide-details rows="3" v-model="form.description_en" placeholder="Description..."/></v-col>
          <v-col cols="12" md="6"><label>AWARD YEAR <span class="required">*</span></label><v-text-field dense outlined hide-details type="number" min="1900" :max="new Date().getFullYear()" v-model.number="form.award_year"/></v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="form-card">
      <div class="form-card-head">Media</div>
      <v-card-text class="pt-4">
        <input ref="awardImageInput" type="file" class="d-none" accept="image/*" @change="onImageChange" />
        <div class="upload-box" @click="openImagePicker" @dragover.prevent @drop.prevent="onDropImage">
          <v-icon class="upload-icon" size="26">mdi-cloud-upload-outline</v-icon>
          Drop award image here or <b>Browse</b><br/><b>Upload to OBS</b>, Max file size: 3 MB
        </div>
        <div v-if="form.image_url" class="image-preview mt-3">
          <img :src="form.image_url" alt="award" @error="onImageError" />
          <button type="button" class="thumb-remove" @click="removeImage">×</button>
        </div>
      </v-card-text>
    </v-card>

    <v-card class="form-card actions-card">
      <v-card-actions class="modal-foot">
        <v-spacer />
        <v-btn outlined @click="onCancel">Cancel</v-btn>
        <v-btn v-if="canSave" color="primary" :loading="isSaving" :disabled="!isFormValid" @click="onSave">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import {mapActions, mapState} from 'vuex'
import {hasPermision} from '@/helper/helper'
import PopupLoading from '@/components/PopupLoading'

const defaultForm = () => ({
  id: null,
  title_th: '',
  title_en: '',
  description_th: '',
  description_en: '',
  award_year: new Date().getFullYear(),
  org_codename: 'group',
  image_url: ''
})

const MAX_IMAGE_SIZE_BYTES = 3 * 1024 * 1024 //3MB
const AWARD_UPLOAD_FOLDER = 'corporate-content/awards'

export default {
  components: {
    'v-popup-loading': PopupLoading
  },
  data() {
    return { form: defaultForm(), initialLoading: false }
  },
  computed: {
    ...mapState('awards', ['list', 'isSaving', 'orgOptions']),
    isPageLoading() { return this.initialLoading },
    isEditMode() { return !!this.$route.params.id },
    canSave() { return this.isEditMode ? hasPermision('CanEditAward') : hasPermision('CanAddAward') },
    scopeOptions() {
      return [{ text: 'Group Central', value: 'group' }, ...this.orgOptions]
    },
    isFormValid() { return !!(this.form.title_th && this.form.title_en && this.form.award_year !== null && this.form.award_year !== undefined) }
  },
  async created() {
    this.initialLoading = true
    try {
      this.setResource('awards')
      await this.fetchOrgOptions()
      await this.fetch()
      if (this.isEditMode) {
        const target = this.list.find(item => String(item.id) === String(this.$route.params.id))
        if (target) {
          this.form = {
            ...defaultForm(),
            ...target,
            org_codename: target.org_codename || 'group'
          }
        }
      }
    } finally {
      this.initialLoading = false
    }
  },
  methods: {
    ...mapActions('awards', ['setResource', 'fetch', 'save', 'fetchOrgOptions', 'upload']),
    onCancel() { this.$router.push({name: 'award-list'}) },
    openImagePicker() {
      this.$refs.awardImageInput && this.$refs.awardImageInput.click()
    },
    fileToDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },
    async uploadImage(file, namePrefix) {
      const base64 = await this.fileToDataUrl(file)
      const uploadName = `${namePrefix}_${Date.now()}`
      return await this.upload({
        name: uploadName,
        base64,
        folder: AWARD_UPLOAD_FOLDER
      })
    },
    async assignImage(file) {
      if (!file || !file.type.startsWith('image/')) return
      if (file.size > MAX_IMAGE_SIZE_BYTES) return
      const imageUrl = await this.uploadImage(file, 'award_image')
      if (!imageUrl) return
      this.form.image_url = imageUrl
    },
    removeImage() {
      this.form.image_url = ''
    },
    onImageError(event) {
      if (event && event.target) {
        event.target.style.display = 'none'
      }
    },
    async onImageChange(event) {
      const file = event.target.files && event.target.files[0]
      await this.assignImage(file)
      event.target.value = null
    },
    async onDropImage(event) {
      const file = event.dataTransfer.files && event.dataTransfer.files[0]
      await this.assignImage(file)
    },
    async onSave() {
      const payload = {
        ...this.form,
        org_codename: this.form.org_codename || 'group'
      }
      const result = await this.$store.dispatch('awards/save', payload)
      if (!result.ok) return
      this.$router.push({name: 'award-list'})
    }
  }
}
</script>

<style scoped>
.contact-page { background: #f7f9fc; min-height: calc(100vh - 120px); }
.head-wrap { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.headline { font-size: 30px !important; color: #0f1f3b; }
.subhead { color: #64748b; font-size: 13px; }
.top-actions { display: flex; align-items: center; }
.form-card { border: 1px solid #e6e9f0; border-radius: 10px; margin-bottom: 14px; background: #fff; overflow: hidden; }
.form-card-head { padding: 12px 14px; border-bottom: 1px solid #edf2fb; font-size: 16px; font-weight: 700; color: #0f1f3b; }
.modal-foot { border-top: 1px solid #edf2fb; padding: 10px 14px; }
.upload-box { border: 1.5px dashed #d9e5f8; border-radius: 10px; color: #7a8cad; padding: 28px; text-align: center; font-size: 13px; cursor: pointer; }
.upload-icon { display: block; margin: 0 auto 8px; color: #9ab0d6; }
.image-preview { width: 180px; height: 120px; border-radius: 8px; border: 1px solid #e5ebf6; overflow: hidden; position: relative; background: #fff; }
.image-preview img { width: 100%; height: 100%; object-fit: cover; }
.thumb-remove { position: absolute; top: 6px; right: 6px; width: 22px; height: 22px; border-radius: 50%; border: 1px solid #f2bcbc; background: #fff; color: #c62828; line-height: 18px; font-size: 14px; cursor: pointer; }
.required { color: #c62828; }
.lang { color: #2962ff; }
label { display:block; font-size:10.5px; color:#64748b; text-transform:uppercase; font-weight:700; margin-bottom:5px; letter-spacing:.4px; }
.actions-card { margin-bottom: 0; }
</style>
